import { type Dayjs } from "dayjs"

import {
  impliedGapDaysFor,
  sourceSideFor,
  targetSideFor
} from "../constants/milestone-dependency-relationship.ts"
import type {
  MilestoneDependency,
  MilestoneDependencyType
} from "../../types/project-milestone-dependency.types.ts"
import type { ProjectMilestone } from "../../types/project-phases.types.tsx"

interface DateWindow {
  start: Dayjs
  end: Dayjs
}

interface ShiftReason {
  sourceMilestoneId: string
  type: MilestoneDependencyType
  lagDays: number
}

interface EffectiveScheduleEntry extends DateWindow {
  /** Whether a dependency pushed this milestone later than its own stored dates. */
  isShifted: boolean
  shiftedByDays: number
  /** The single dependency edge that ultimately determined the effective start —
   * null when the milestone isn't shifted, even if it has dependencies. */
  shiftReason: ShiftReason | null
  /** When actually finished — a separate fact from `end` (the planned/
   * effective end), never a substitute for it. Null while still in progress. */
  actualEnd: Dayjs | null
}

const anchorOf = (window: DateWindow, side: "start" | "end") =>
  side === "start" ? window.start : window.end

/**
 * The single source of truth for what a milestone's dates actually are once
 * its dependencies are taken into account. Both the Gantt and the Table read
 * from this rather than a milestone's raw Planned Start/End directly — a
 * milestone whose stored dates would otherwise violate a dependency (or did,
 * before an upstream milestone's dates changed) is reported at the earliest
 * date that satisfies every dependency it has, never at a date that visibly
 * precedes something it depends on.
 *
 * `overrides` lets a caller preview "what would the schedule look like if
 * milestone X had these dates / this dependency existed" without touching
 * real data — used to compute the impact of a pending edit before it's saved.
 */
interface EffectiveScheduleOverride extends Partial<DateWindow> {
  /** Explicit `null` clears a milestone's actual end for the preview;
   * `undefined` (the default, i.e. the key simply absent) leaves its real
   * stored value alone. */
  actualEnd?: Dayjs | null
}

function calculateEffectiveSchedule({
  milestones,
  dependencies,
  parseDate,
  overrides
}: {
  milestones: ProjectMilestone[]
  dependencies: MilestoneDependency[]
  parseDate: (value: unknown) => Dayjs | null
  overrides?: Map<string, EffectiveScheduleOverride>
}): Map<string, EffectiveScheduleEntry> {
  const baseline = new Map<string, DateWindow>()
  // Once a milestone has actually finished, that's the real anchor a
  // Finish-based dependent must schedule off of — its Planned End keeps
  // representing the original plan and is never overwritten by this.
  const actualEndById = new Map<string, Dayjs>()

  milestones.forEach((milestone) => {
    const override = overrides?.get(milestone.id)
    const start = override?.start ?? parseDate(milestone.start_date)
    const end = override?.end ?? parseDate(milestone.end_date)
    if (start && end) baseline.set(milestone.id, { start, end })

    const actualEnd =
      override && "actualEnd" in override
        ? override.actualEnd
        : parseDate(milestone.actual_end_date)
    if (actualEnd) actualEndById.set(milestone.id, actualEnd)
  })

  const incomingByTarget = new Map<string, MilestoneDependency[]>()
  const outgoingBySource = new Map<string, string[]>()
  const indegree = new Map<string, number>()

  dependencies.forEach((dep) => {
    // A dependency touching a milestone with no dates of its own imposes
    // nothing yet — there's no anchor to compute a floor from.
    if (!baseline.has(dep.source_milestone) || !baseline.has(dep.target_milestone)) {
      return
    }
    incomingByTarget.set(dep.target_milestone, [
      ...(incomingByTarget.get(dep.target_milestone) || []),
      dep
    ])
    outgoingBySource.set(dep.source_milestone, [
      ...(outgoingBySource.get(dep.source_milestone) || []),
      dep.target_milestone
    ])
    indegree.set(dep.target_milestone, (indegree.get(dep.target_milestone) || 0) + 1)
  })

  // Kahn's algorithm: sources before targets, so every node is only computed
  // once all of its incoming edges' sources already have an effective window.
  const order: string[] = []
  const remainingIndegree = new Map(indegree)
  const queue: string[] = []
  baseline.forEach((_, id) => {
    if (!remainingIndegree.get(id)) queue.push(id)
  })
  while (queue.length) {
    const id = queue.shift() as string
    order.push(id)
    for (const nextId of outgoingBySource.get(id) || []) {
      const remaining = (remainingIndegree.get(nextId) || 0) - 1
      remainingIndegree.set(nextId, remaining)
      if (remaining === 0) queue.push(nextId)
    }
  }
  // Defensive only — the backend rejects circular dependencies at creation,
  // so this should never trigger, but a stray leftover node is still safer
  // computed on its own baseline than silently dropped from the schedule.
  baseline.forEach((_, id) => {
    if (!order.includes(id)) order.push(id)
  })

  const effective = new Map<string, EffectiveScheduleEntry>()

  order.forEach((id) => {
    const own = baseline.get(id) as DateWindow
    const edges = incomingByTarget.get(id) || []

    if (!edges.length) {
      effective.set(id, {
        ...own,
        isShifted: false,
        shiftedByDays: 0,
        shiftReason: null,
        actualEnd: actualEndById.get(id) ?? null
      })
      return
    }

    const durationDays = own.end.diff(own.start, "day")

    let startFloor: Dayjs | null = null
    let startFloorEdge: MilestoneDependency | null = null
    let endFloor: Dayjs | null = null
    let endFloorEdge: MilestoneDependency | null = null

    edges.forEach((dep) => {
      const sourceEffective = effective.get(dep.source_milestone)
      if (!sourceEffective) return

      const sourceSide = sourceSideFor(dep.type)
      // Once the source has actually finished, a Finish-based edge (FS, FF)
      // schedules off that real finish rather than the plan — the source's
      // own Planned End is untouched, only what its dependents anchor to
      // changes. Start-based edges (SS, SF) are unaffected: completion status
      // says nothing about when something started.
      const sourceActualEnd = actualEndById.get(dep.source_milestone)
      const sourceAnchor =
        sourceSide === "end" && sourceActualEnd
          ? sourceActualEnd
          : anchorOf(sourceEffective, sourceSide)

      // An explicit lag IS the gap the user configured — it doesn't stack on
      // top of the implicit one-day Finish→Start gap. Lag 0 falls back to
      // that implicit gap (1 day for FS, 0 for the same-day-capable types);
      // any non-zero lag (including negative, i.e. overlap) is used as-is.
      const anchor = sourceAnchor.add(
        dep.lag_days || impliedGapDaysFor(dep.type),
        "day"
      )

      if (targetSideFor(dep.type) === "start") {
        if (!startFloor || anchor.isAfter(startFloor)) {
          startFloor = anchor
          startFloorEdge = dep
        }
      } else {
        if (!endFloor || anchor.isAfter(endFloor)) {
          endFloor = anchor
          endFloorEdge = dep
        }
      }
    })

    // A milestone is never pulled earlier than its own stored start — only
    // ever pushed later, and only as far as the strictest constraint demands.
    let effectiveStart = own.start
    let winningEdge: MilestoneDependency | null = null

    if (startFloor && (startFloor as Dayjs).isAfter(effectiveStart)) {
      effectiveStart = startFloor
      winningEdge = startFloorEdge
    }
    if (endFloor) {
      const requiredStartFromEnd = (endFloor as Dayjs).subtract(durationDays, "day")
      if (requiredStartFromEnd.isAfter(effectiveStart)) {
        effectiveStart = requiredStartFromEnd
        winningEdge = endFloorEdge
      }
    }

    const effectiveEnd = effectiveStart.add(durationDays, "day")
    const shiftedByDays = effectiveStart.diff(own.start, "day")

    effective.set(id, {
      start: effectiveStart,
      end: effectiveEnd,
      isShifted: shiftedByDays > 0,
      shiftedByDays,
      shiftReason:
        shiftedByDays > 0 && winningEdge
          ? {
              sourceMilestoneId: (winningEdge as MilestoneDependency).source_milestone,
              type: (winningEdge as MilestoneDependency).type,
              lagDays: (winningEdge as MilestoneDependency).lag_days
            }
          : null,
      actualEnd: actualEndById.get(id) ?? null
    })
  })

  return effective
}

/**
 * Compares two effective-schedule results (typically "before" vs "after" a
 * pending edit, dependency creation, or dependency change) and reports every
 * milestone whose calculated dates actually differ — the same shape used to
 * both drive and word the "this will also move N milestones" confirmation.
 * `previousWindow` carries the "before" value so a confirmation can show the
 * actual before → after effect rather than just the destination.
 */
function diffEffectiveSchedule(
  before: Map<string, EffectiveScheduleEntry>,
  after: Map<string, EffectiveScheduleEntry>,
  options?: { excludeIds?: Set<string> }
): { id: string; window: DateWindow; previousWindow: DateWindow | null }[] {
  const changed: { id: string; window: DateWindow; previousWindow: DateWindow | null }[] = []
  after.forEach((entry, id) => {
    if (options?.excludeIds?.has(id)) return
    const previous = before.get(id)
    if (!previous || !previous.start.isSame(entry.start, "day") || !previous.end.isSame(entry.end, "day")) {
      changed.push({
        id,
        window: { start: entry.start, end: entry.end },
        previousWindow: previous ? { start: previous.start, end: previous.end } : null
      })
    }
  })
  return changed
}

export { calculateEffectiveSchedule, diffEffectiveSchedule }
export type { DateWindow, EffectiveScheduleEntry, EffectiveScheduleOverride, ShiftReason }
